import { useAvailability } from '~/composables/useAvailability';

export function useEventSchema() {
  const runtimeConfig = useRuntimeConfig();
  const siteUrl = runtimeConfig.public.siteUrl;
  const timeZone = 'Europe/Oslo';
  const availability = useAvailability();

  // Cache for generated schemas to avoid duplicate processing
  const schemaCache = new Map<string, any>();

  const generateEventSchema = (event: any, lang: 'no' | 'en') => {
    if (!event || !event.content) {
      console.warn('Cannot generate schema: Invalid event data provided');
      return null;
    }

    const cacheKey = `${event.uuid || event.id}-${lang}`;

    // Return cached schema if available
    if (schemaCache.has(cacheKey)) {
      return schemaCache.get(cacheKey);
    }

    try {
      const syncProduction = Array.isArray(event.content.SyncProduction)
        ? event.content.SyncProduction.find(
            (prod: any) => prod.full_slug && prod.full_slug.startsWith(`${lang}/`),
          )
        : null;

      const productionContent = syncProduction?.content || {};

      if (!event.content.SyncEventStartTime) {
        console.warn('Missing start time for event', event.name || event.id);
        return null;
      }

      // Add 2 hours to correct the time offset as the dates we are getting from storyblok are two hours behind what they should be
      const correctTime = (date: Date) => new Date(date.getTime() + 2 * 60 * 60 * 1000);

      const startDateRaw = new Date(event.content.SyncEventStartTime);
      const endDateRaw = event.content.SyncEventEndTime
        ? new Date(event.content.SyncEventEndTime)
        : new Date(startDateRaw.getTime() + 2 * 60 * 60 * 1000);

      const startDateCorrected = correctTime(startDateRaw);
      const endDateCorrected = correctTime(endDateRaw);

      const formatDateToISO = (date: Date) => {
        const formatter = new Intl.DateTimeFormat('en-GB', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

        const parts = formatter.formatToParts(date);
        const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';

        return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
      };

      const processOffers = () => {
        if (
          !event.content.SyncPricing ||
          !Array.isArray(event.content.SyncPricing) ||
          event.content.SyncPricing.length === 0
        ) {
          return {
            '@type': 'Offer',
            url:
              syncProduction && syncProduction.full_slug
                ? `${siteUrl}/${syncProduction.full_slug}`
                : `${siteUrl}`,
            priceCurrency: 'NOK',
            availability:
              availability.state.value.events[event.content.SyncId] === 2
                ? 'https://schema.org/SoldOut'
                : 'https://schema.org/InStock',
          };
        }

        const allPrices: number[] = [];
        event.content.SyncPricing.forEach((item: any) => {
          if (item.PricingType === 'Ledsager') return;

          ['PriceZoneA', 'PriceZoneB', 'PriceZoneC'].forEach((zone) => {
            const price = parseFloat(item[zone]);
            if (!isNaN(price) && price >= 0) {
              allPrices.push(price);
            }
          });
        });

        if (allPrices.length === 0) return null;

        allPrices.sort((a, b) => a - b);

        return {
          '@type': 'AggregateOffer',
          url:
            event.content.SyncBookingLink?.url ||
            (syncProduction && syncProduction.full_slug
              ? `${siteUrl}/${syncProduction.full_slug}`
              : `${siteUrl}`),
          lowPrice: allPrices[0],
          highPrice: allPrices[allPrices.length - 1],
          priceCurrency: 'NOK',
          availability:
            availability.state.value.events[event.content.SyncId] === 2
              ? 'https://schema.org/SoldOut'
              : 'https://schema.org/InStock',
        };
      };

      const schema = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: event.name || event.content.SyncName || 'Event',
        startDate: formatDateToISO(startDateCorrected),
        endDate: formatDateToISO(endDateCorrected),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: {
          '@type': 'Place',
          name: event.content.SyncScene || 'Venue',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Bergen',
            addressRegion: 'Vestland',
            addressCountry: 'NO',
          },
        },
        organizer: {
          '@type': 'Organization',
          name: event.content.SyncOrganiser || 'Festspillene i Bergen',
          url: 'https://fib.no',
        },
      };

      const offers = processOffers();
      if (offers) {
        Object.assign(schema, { offers });
      }

      if (productionContent.thumbnail?.filename) {
        Object.assign(schema, {
          image: [productionContent.thumbnail.filename],
        });
      }

      if (productionContent.excerpt) {
        Object.assign(schema, {
          description: productionContent.excerpt,
        });
      }

      if (productionContent.Subtitle) {
        Object.assign(schema, {
          performer: {
            '@type': 'PerformingGroup',
            name: productionContent.Subtitle,
          },
        });
      }

      const result = JSON.parse(JSON.stringify(schema));
      schemaCache.set(cacheKey, result);

      return result;
    } catch (error) {
      console.error('Error generating event schema:', error);
      return null;
    }
  };

  return {
    generateEventSchema,
  };
}
